import { z } from "zod";
import { isoDateTimeSchema, uuidSchema } from "./common.ts";

/**
 * Cover requests between staff (Pilates studio shape, S3; migration 0072). A staff member asks for
 * cover for one session. The other staff of that class get the offer. The first yes takes the
 * session. With "Approve each cover" on, the owner approves or refuses the yes first.
 *
 *   open               waits for a yes
 *   awaiting_approval  a person said yes; the owner approves or refuses
 *   taken              the person who said yes runs the session now
 *   cancelled          taken back, or the session was cancelled, moved or got another staff member
 *   expired            nobody took it before the session started
 */
export const coverRequestStatuses = ["open", "awaiting_approval", "taken", "cancelled", "expired"] as const;
export const coverRequestStatusSchema = z.enum(coverRequestStatuses);
export type CoverRequestStatus = z.infer<typeof coverRequestStatusSchema>;

/** One cover request as the person who reads it sees it. The flags are for that person. */
export const coverRequestSchema = z.object({
  id: uuidSchema,
  sessionId: uuidSchema,
  groupName: z.string(),
  startsAt: isoDateTimeSchema,
  endsAt: isoDateTimeSchema,
  status: coverRequestStatusSchema,
  requestedById: uuidSchema,
  requestedByName: z.string().nullable(),
  takenById: uuidSchema.nullable(),
  takenByName: z.string().nullable(),
  /** How many people got the offer, and how many said "Not me". */
  offeredCount: z.number().int().min(0),
  declinedCount: z.number().int().min(0),
  createdAt: isoDateTimeSchema,
  answeredAt: isoDateTimeSchema.nullable(),
  /** The reader asked for this cover. */
  mine: z.boolean(),
  /** The reader said yes (it waits for approval, or the reader runs the session now). */
  takenByMe: z.boolean(),
  /** The offer waits for the reader: Take it or Not me. */
  offeredToMe: z.boolean(),
  /** The reader can take the request back (the person who asked, or the owner or an admin). */
  canCancel: z.boolean(),
  /** The reader approves or refuses the yes (the owner or an admin, while it waits for approval). */
  canDecide: z.boolean(),
});
export type CoverRequest = z.infer<typeof coverRequestSchema>;

/**
 * `GET /business/businesses/:id/covers`: the cover requests of sessions that did not end yet. The
 * owner and admins see every one; a staff member sees the ones they asked, took, or can answer.
 */
export const coverRequestsResponseSchema = z.object({
  items: z.array(coverRequestSchema),
  /** The business setting "Approve each cover". */
  needsApproval: z.boolean(),
});
export type CoverRequestsResponse = z.infer<typeof coverRequestsResponseSchema>;

/** `POST .../covers/:coverId/decide`. Owner and admin only. */
export const decideCoverRequestSchema = z.object({ decision: z.enum(["approve", "refuse"]) });
export type DecideCoverRequest = z.infer<typeof decideCoverRequestSchema>;
